import { useCallback, useState } from 'react';
import {
  updateContributorMedia,
  UpdateContributorMediaRequest,
  uploadContributorMedia,
  UploadFileResponse,
} from '../api/contributorMedia';
import { useMusicState } from './storeHooks/useMusicState';

interface UseContributorMediaUploadReturn {
  uploadFile: (
    file: File,
    contributorId: string,
    mediaType: 'thumbnail' | 'desktop' | 'mobile',
  ) => Promise<UploadFileResponse>;
  saveChanges: (
    contributorId: string,
    mediaData: UpdateContributorMediaRequest,
  ) => Promise<any>;
  uploading: boolean;
  saving: boolean;
  error: string | null;
  resetError: () => void;
}

export const useContributorMediaUpload =
  (): UseContributorMediaUploadReturn => {
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { updateAlbumContributorMedia } = useMusicState();

    const uploadFile = useCallback(
      async (
        file: File,
        contributorId: string,
        mediaType: 'thumbnail' | 'desktop' | 'mobile',
      ): Promise<UploadFileResponse> => {
        setUploading(true);
        setError(null);

        try {
          const result = await uploadContributorMedia(
            file,
            contributorId,
            mediaType,
          );
          return result;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Upload failed';
          setError(errorMessage);
          throw err;
        } finally {
          setUploading(false);
        }
      },
      [],
    );

    const saveChanges = useCallback(
      async (
        contributorId: string,
        mediaData: UpdateContributorMediaRequest,
      ) => {
        setSaving(true);
        setError(null);

        try {
          const result = await updateContributorMedia(contributorId, mediaData);

          // Update the Redux store with the new media URLs
          updateAlbumContributorMedia({ contributorId, mediaData });

          return result;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Save failed';
          setError(errorMessage);
          throw err;
        } finally {
          setSaving(false);
        }
      },
      [updateAlbumContributorMedia],
    );

    const resetError = useCallback(() => {
      setError(null);
    }, []);

    return {
      uploadFile,
      saveChanges,
      uploading,
      saving,
      error,
      resetError,
    };
  };
